import React from "react";
import { Lock, ShieldCheck, KeyRound, FileLock2 } from "lucide-react";
import Eyebrow from "@/components/Eyebrow";
import { FadeUp } from "@/components/Motion";
import CTABlock from "@/components/CTABlock";

const pillars = [
  { icon: ShieldCheck, title: "Secure systems", body: "Modern, vetted accounting stacks (QuickBooks Online, Xero) with 2FA enforced across all sessions." },
  { icon: Lock, title: "Encrypted data handling", body: "TLS in transit; provider-side encryption at rest. No plain-text exports, ever." },
  { icon: KeyRound, title: "Controlled access", body: "Role-based access, shortest-necessary credentials, and offboarding within 24 hours of engagement changes." },
  { icon: FileLock2, title: "Organized documentation", body: "Client documents live in a single, access-controlled vault, not email attachments." },
];

export default function Security() {
  return (
    <div data-testid="security-page">
      <section className="container-editorial py-20 md:py-28 rule-bottom" data-testid="security-header">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-8">
            <Eyebrow>Security · Data Protection</Eyebrow>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1] text-[#0C1D36] mt-6">
              Your financial data is <em className="text-[#BA1A1A] not-italic">protected</em>, not just managed.
            </h1>
          </div>
          <div className="md:col-span-4 md:pt-10">
            <p className="text-lg text-[#0C1D36]/80">
              You're trusting us with sensitive financial information. We take that seriously.
            </p>
          </div>
        </div>
      </section>

      <section className="container-editorial py-24 md:py-32" data-testid="security-pillars">
        <div className="grid md:grid-cols-2 gap-px bg-[#0C1D36]/12">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <FadeUp
                key={p.title}
                className="bg-white p-10 md:p-12"
                data-testid={`security-pillar-${i}`}
              >
                <Icon className="w-8 h-8 text-[#BA1A1A]" />
                <div className="eyebrow-muted mt-8">Pillar 0{i + 1}</div>
                <h3 className="font-serif text-3xl text-[#0C1D36] mt-3 leading-tight">{p.title}</h3>
                <p className="mt-5 text-[#4B5563] leading-relaxed max-w-md">{p.body}</p>
              </FadeUp>
            );
          })}
        </div>

        <div className="mt-20 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <Eyebrow>Our Posture</Eyebrow>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05] mt-6">
              Protection is part of the service, not an add-on.
            </h2>
          </div>
          <div className="md:col-span-7 md:border-l md:border-[#0C1D36]/15 md:pl-12">
            <p className="text-lg text-[#0C1D36]/80 dropcap">
              A law firm's financials are uniquely sensitive. They include client-fund
              balances, matter identifiers, and partner-level economics. Our controls are
              designed around that reality, not retrofitted from a generic bookkeeping
              template.
            </p>
          </div>
        </div>
      </section>

      <CTABlock
        eyebrow="Security Review"
        title="Want to see the controls in place before you share a single document?"
        ctaLabel="Request a Security Walkthrough"
      />
    </div>
  );
}
